import React from 'react';
import { Box, Typography } from '@mui/material';
import { LearnerWhatsNew, whatsNewText } from './learnerWhatsNew';

/**
 * The What's New notice text: the New paragraph, the Updated paragraph, then
 * the numbered actions. Shared by the learner pop-up and the read-only example
 * in the Publish Update modal, so the admin sees exactly what learners read.
 */

const FONT = 'Inter, sans-serif';

interface Props {
  whatsNew: LearnerWhatsNew;
  /** Text colours for the surrounding surface (both are dark). */
  color?: string;
  mutedColor?: string;
  fontSize?: number;
  /** Render as a quotation — italic, inside “ ” — e.g. the admin's example. */
  quoted?: boolean;
}

const NO_ACTIONS = 'There are no actions you need to complete.';

const WhatsNewMessage: React.FC<Props> = ({
  whatsNew,
  color = '#FDFDFD',
  mutedColor = '#B6B6B6',
  fontSize = 14,
  quoted = false
}) => {
  const { newParagraph, updatedParagraph, actions } = whatsNewText(whatsNew);
  const lineHeight = `${Math.round(fontSize * 1.55)}px`;
  const text = { fontFamily: FONT, fontSize, lineHeight, color, fontStyle: quoted ? 'italic' : 'normal' };

  // Quotation marks open the first line and close the last one.
  const paragraphs = [newParagraph, updatedParagraph].filter((p): p is string => Boolean(p));
  const open = (s: string, first: boolean) => (quoted && first ? `“${s}` : s);
  const close = (s: string, last: boolean) => (quoted && last ? `${s}”` : s);

  // The actions are the highlight: the New / Updated paragraphs sit back in the
  // muted colour, the actions (or "no actions") line is bold in the main one.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {paragraphs.map((p, i) => (
        <Typography key={p} sx={{ ...text, color: mutedColor }}>
          {open(p, i === 0)}
        </Typography>
      ))}
      {actions.length ? (
        <Box>
          <Typography sx={{ ...text, fontWeight: 600 }}>
            {open('The actions you need to complete:', paragraphs.length === 0)}
          </Typography>
          <Box
            component="ol"
            sx={{ m: 0, mt: '6px', pl: '22px', display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            {actions.map((a, i) => (
              <Typography key={a} component="li" sx={text}>
                {close(a, i === actions.length - 1)}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography sx={{ ...text, fontWeight: 600 }}>
          {close(open(NO_ACTIONS, paragraphs.length === 0), true)}
        </Typography>
      )}
    </Box>
  );
};

export default WhatsNewMessage;
